import { Plus, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui";
import type {
  CompoundRecordComposerSearchCondition,
  CompoundRecordComposerSearchField,
} from "~/features/compoundRecordComposers/types";

interface CompoundRecordComposersSearchControlsProps {
  conditions: CompoundRecordComposerSearchCondition[];
  onConditionsChange: (next: CompoundRecordComposerSearchCondition[]) => void;
  onPageReset: () => void;
}

const FIELDS: { value: CompoundRecordComposerSearchField; labelKey: string }[] = [
  { value: "id", labelKey: "model.common.id" },
  { value: "key", labelKey: "model.common.key" },
  { value: "recordType", labelKey: "model.common.recordType" },
];

export function CompoundRecordComposersSearchControls({
  conditions,
  onConditionsChange,
  onPageReset,
}: CompoundRecordComposersSearchControlsProps) {
  const { t } = useTranslation();

  const [field, setField] = useState<CompoundRecordComposerSearchField>("id");
  const [term, setTerm] = useState("");
  const [exactMatch, setExactMatch] = useState(true);

  const isIdField = field === "id";

  const handleFieldChange = (next: string) => {
    const f = FIELDS.find((field) => field.value === next)?.value;
    if (!f) return;
    setField(f);
    setTerm("");
    if (f === "id") setExactMatch(true);
  };

  const handleAdd = () => {
    if (!term.trim()) return;
    const next: CompoundRecordComposerSearchCondition = {
      id: `${field}-${term}-${Date.now()}`,
      field,
      value: term.trim(),
      exactMatch: isIdField ? true : exactMatch,
    };
    onConditionsChange([...conditions, next]);
    setTerm("");
    onPageReset();
  };

  const handleRemove = (id: string) => {
    onConditionsChange(conditions.filter((c) => c.id !== id));
    onPageReset();
  };

  const handleClearAll = () => {
    onConditionsChange([]);
    onPageReset();
  };

  const currentFieldLabel = t(FIELDS.find((f) => f.value === field)?.labelKey ?? "model.common.id");

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={field} onValueChange={handleFieldChange}>
          <SelectTrigger className="h-9 w-[160px] text-14-regular">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FIELDS.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {t(f.labelKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          placeholder={t("ui.searchByX", { x: currentFieldLabel })}
          className="h-9 w-[360px]"
        />

        <label className="flex cursor-pointer select-none items-center gap-2 text-14-regular text-fg-2">
          <button
            type="button"
            role="switch"
            aria-checked={isIdField ? true : exactMatch}
            onClick={() => !isIdField && setExactMatch((v) => !v)}
            disabled={isIdField}
            className={[
              "relative inline-flex h-[22px] w-10 shrink-0 rounded-full border-2 border-transparent",
              "transition-colors duration-200 ease-in-out",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50 focus-visible:ring-offset-1",
              "disabled:cursor-not-allowed disabled:opacity-50",
              isIdField || exactMatch ? "bg-primary-500" : "bg-fg-3/30",
            ].join(" ")}
          >
            <span
              className={[
                "pointer-events-none inline-block h-[18px] w-[18px] rounded-full bg-white",
                "shadow-sm ring-0 transition-transform duration-200 ease-in-out",
                isIdField || exactMatch ? "translate-x-[18px]" : "translate-x-0",
              ].join(" ")}
            />
          </button>
          <span className={isIdField ? "text-fg-3" : ""}>{t("ui.exactMatch")}</span>
        </label>

        <Button type="button" variant="outline" size="sm" onClick={handleAdd} disabled={!term.trim()}>
          <Plus />
          <span>{t("ui.addCondition")}</span>
        </Button>
      </div>

      {conditions.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-12-regular text-fg-3">{t("ui.searchConditions")}:</span>
          {conditions.map((c) => {
            const fieldLabel = t(FIELDS.find((f) => f.value === c.field)?.labelKey ?? "model.common.id");
            const op = c.field === "id" || c.exactMatch ? "=" : "contains";
            return (
              <span
                key={c.id}
                className="inline-flex items-center gap-1 rounded-full border border-primary-300 bg-primary-50 px-2.5 py-0.5 text-12-regular text-primary-600"
              >
                {fieldLabel} {op} &ldquo;{c.value}&rdquo;
                <button
                  type="button"
                  onClick={() => handleRemove(c.id)}
                  className="ml-0.5 rounded-full p-0.5 hover:bg-primary-100"
                  aria-label={t("ui.delete")}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
          <button
            type="button"
            onClick={handleClearAll}
            className="text-12-regular text-fg-3 underline-offset-2 hover:text-fg-1 hover:underline"
          >
            {t("ui.clearAll")}
          </button>
        </div>
      )}
    </div>
  );
}
